import React from 'react';
import { Card, Space, Select, DatePicker, Radio, Button, Descriptions, Typography } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

export interface SellerMapping {
    id: number;
    sku_code: string;
    shop_name: string;
    title?: string;
    asin?: string;
    internal_name?: string;
    internal_name_cn?: string;
}

interface AnalysisFilterBarProps {
    sellerMappings: SellerMapping[];
    selectedSellerSkuId?: number;
    onSelectedSellerSkuIdChange: (val: number) => void;

    dates: [dayjs.Dayjs, dayjs.Dayjs] | null;
    onDatesChange: (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;

    country: string;
    onCountryChange: (val: string) => void;

    salesChannel: string;
    onSalesChannelChange: (val: string) => void;

    granularity: 'day' | 'week' | 'month';
    onGranularityChange: (val: 'day' | 'week' | 'month') => void;

    onRefresh: () => void;
    loading: boolean;

    currentMapping?: SellerMapping;
}

export const AnalysisFilterBar: React.FC<AnalysisFilterBarProps> = ({
    sellerMappings, selectedSellerSkuId, onSelectedSellerSkuIdChange,
    dates, onDatesChange, country, onCountryChange, salesChannel, onSalesChannelChange,
    granularity, onGranularityChange, onRefresh, loading, currentMapping
}) => {

    const getDisplayTitle = (mapping: SellerMapping) => {
        if (mapping.title && mapping.title !== 'Unknown Product') return mapping.title;
        return mapping.internal_name_cn || mapping.internal_name || '-';
    };

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: currentMapping ? 12 : 0 }}>
                <Space>
                    <span style={{ fontWeight: 500 }}>分析对象:</span>
                    {sellerMappings.length > 1 ? (
                        <Select value={selectedSellerSkuId} onChange={onSelectedSellerSkuIdChange} style={{ width: 250 }}>
                            {sellerMappings.map(m => (
                                <Option key={m.id} value={m.id}>{m.sku_code} ({m.shop_name})</Option>
                            ))}
                        </Select>
                    ) : (
                        <Text strong style={{ fontSize: 16 }}>
                            {currentMapping ? `${currentMapping.sku_code} (${currentMapping.shop_name})` : '加载中...'}
                        </Text>
                    )}
                </Space>

                <Space>
                    <RangePicker
                        onChange={(d) => onDatesChange(d as any)} value={dates} allowClear={false}
                        presets={[
                            { label: '过去7天', value: [dayjs().subtract(7, 'd'), dayjs()] },
                            { label: '过去30天', value: [dayjs().subtract(30, 'd'), dayjs()] },
                            { label: '本月', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                            { label: '上月', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                            { label: '今年', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                        ]}
                    />
                    <Select placeholder="国家" style={{ width: 80 }} value={country} onChange={onCountryChange} options={['US', 'CA', 'MX', 'GB', 'DE', 'FR', 'IT', 'ES', 'JP'].map(c => ({ label: c, value: c }))} />
                    <Select placeholder="渠道" style={{ width: 140 }} value={salesChannel} onChange={onSalesChannelChange} allowClear options={['Amazon.com', 'Amazon.ca', 'Amazon.com.mx', 'Non-Amazon'].map(c => ({ label: c, value: c }))} />
                    <Radio.Group value={granularity} onChange={(e) => onGranularityChange(e.target.value)} buttonStyle="solid">
                        <Radio.Button value="day">日</Radio.Button>
                        <Radio.Button value="week">周</Radio.Button>
                        <Radio.Button value="month">月</Radio.Button>
                    </Radio.Group>
                    <Button type="primary" onClick={onRefresh} loading={loading}>刷新数据</Button>
                </Space>
            </div>

            {currentMapping && (
                <Descriptions size="small" column={4} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="品名">{getDisplayTitle(currentMapping)}</Descriptions.Item>
                    <Descriptions.Item label="ASIN">
                        <a href={`https://www.amazon.com/dp/${currentMapping.asin}`} target="_blank" rel="noopener noreferrer">{currentMapping.asin}</a>
                    </Descriptions.Item>
                    <Descriptions.Item label="店铺">{currentMapping.shop_name}</Descriptions.Item>
                    <Descriptions.Item label="平台SKU">{currentMapping.sku_code}</Descriptions.Item>
                </Descriptions>
            )}
        </Card>
    );
};
